import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import supportChatService, {
  type SupportMessage,
  type SupportThread,
} from '@/services/supportChatService'
import { useSupportChatEvents } from '@/hooks/useSupportChatEvents'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useSupportUnreadStore } from '@/stores/supportUnreadStore'
import { hasModuleView, isPlatformAdmin } from '@/lib/dashboardAccess'
import { useShopPreferences } from '@/hooks/useShopPreferences'

/**
 * Keeps sidebar/header support badges fresh across the dashboard, and toasts
 * when a counterpart message arrives while the user is on another page.
 */
export function useSupportInboxAlerts(opts?: { isCustomer?: boolean }) {
  const isCustomer = Boolean(opts?.isCustomer)
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { permissions, role } = usePermissionsStore()
  const { shop } = useShopPreferences()
  const setUnread = useSupportUnreadStore((s) => s.setUnread)
  const reset = useSupportUnreadStore((s) => s.reset)

  const shopId = user?.shop_id
  const supportChatEnabled = shop?.support_chat_enabled !== false
  const onSupportPage = isCustomer
    ? location.pathname.startsWith('/customer/support')
    : location.pathname.startsWith('/dashboard/support')
  const staffRole = role ?? user?.role
  const staffPermissionsReady =
    isCustomer || isPlatformAdmin(staffRole) || permissions != null

  const canListen =
    isAuthenticated &&
    Boolean(shopId) &&
    supportChatEnabled &&
    (isCustomer
      ? Boolean(user?.is_web_user)
      : !user?.is_web_user &&
        staffPermissionsReady &&
        hasModuleView(permissions, 'support_chat', staffRole))

  const refreshUnread = useCallback(async () => {
    if (!shopId || !canListen) return
    try {
      const res = await supportChatService.getUnread(shopId)
      if (res.success && res.data) {
        setUnread(res.data.unread_threads, res.data.unread_messages)
      }
    } catch {
      /* ignore — optional alert channel */
    }
  }, [shopId, canListen, setUnread])

  useEffect(() => {
    if (!isCustomer && !staffPermissionsReady) return
    if (!canListen) {
      reset()
      return
    }
    void refreshUnread()
    const id = window.setInterval(() => void refreshUnread(), 30000)
    return () => window.clearInterval(id)
  }, [canListen, refreshUnread, reset, isCustomer, staffPermissionsReady])

  // Re-fetch when entering/leaving the support page (reads mark messages clear)
  useEffect(() => {
    if (!canListen) return
    void refreshUnread()
  }, [onSupportPage, canListen, refreshUnread])

  const lastToastAtRef = useRef(0)

  useSupportChatEvents({
    shopId,
    // Inbox/CustomerSupport already open a shop stream on their pages — avoid a second
    // long-lived EventSource that can starve REST calls (browser ~6 connections/host).
    enabled: canListen && !onSupportPage,
    onEvent: (event) => {
      if (event.type === 'connected') return
      void refreshUnread()

      if (onSupportPage) return
      if (event.type !== 'message.created' && event.type !== 'thread.created') return

      const payload = event.payload as
        | {
            message?: SupportMessage
            thread?: SupportThread
            is_platform_ticket?: boolean
          }
        | undefined
      if (!isCustomer && (payload?.is_platform_ticket || payload?.thread?.is_platform_ticket)) {
        return
      }
      const message = payload?.message
      const thread = payload?.thread

      // Only notify for inbound messages from the other party
      if (isCustomer) {
        if (event.type === 'message.created' && message?.sender_type !== 'staff') return
        if (event.type === 'thread.created' && thread?.created_by_type === 'customer') return
      } else {
        if (event.type === 'message.created') {
          if (message?.sender_type !== 'customer' || message.is_internal) return
        }
        if (event.type === 'thread.created' && thread?.created_by_type !== 'customer') return
      }

      const now = Date.now()
      if (now - lastToastAtRef.current < 2500) return
      lastToastAtRef.current = now

      const preview =
        message?.body?.trim() ||
        thread?.subject?.trim() ||
        thread?.last_message_preview?.trim() ||
        ''
      const short = preview.length > 80 ? `${preview.slice(0, 79)}…` : preview

      if (isCustomer) {
        toast('You have a new support message', {
          id: 'support-inbound',
          icon: '💬',
          duration: 5000,
        })
      } else {
        const who = thread?.customer_name || message?.sender_name || 'A customer'
        toast(`${who} sent a support message${short ? `: ${short}` : ''}`, {
          id: 'support-inbound',
          icon: '💬',
          duration: 6000,
        })
      }
    },
  })
}
